// MUI-free copy of Ui-components/display-image for the plain layout (header/footer): same next/image and SVG
// handling, minus the Emotion `sx` prop (style it with className instead).
import React, {memo} from 'react';
import Image, {StaticImageData} from 'next/image';

type CommonProps = {
    alt: string;
    /** Serve the file as-is (no /_next/image optimizer), e.g. tiny CDN images. */
    unoptimized?: boolean;
    sizes?: string;
    placeholder?: 'blur' | 'empty';
    priority?: boolean;
    quality?: number;
    className?: string;
    style?: React.CSSProperties;
    blurDataURL?: string;
    suppressHydrationWarning?: boolean;
    loading?: 'lazy' | 'eager';
    onLoad?: React.ReactEventHandler<HTMLImageElement>;
    onClick?: React.MouseEventHandler<HTMLImageElement>;
    fetchPriority?: 'high' | 'low' | 'auto';
};

/** Static import — next/image derives intrinsic dimensions automatically, no width/height required */
type StaticSrc = CommonProps & {
    src: StaticImageData;
    width?: number;
    height?: number;
    fill?: boolean;
};

/** String URL or mixed src — must provide explicit width + height to prevent layout shift (CLS) */
type StringSrcWithDimensions = CommonProps & {
    src: string | StaticImageData;
    width: number;
    height: number;
    fill?: never;
};

/** Fill mode — container must be position:relative/absolute/fixed with explicit dimensions */
type FillMode = CommonProps & {
    src: string | StaticImageData;
    fill: true;
    width?: never;
    height?: never;
};

export type OptimizedImageProps = StaticSrc | StringSrcWithDimensions | FillMode;

const isSvgSrc = (src: string | StaticImageData): boolean => {
    if (typeof src === 'string') {
        return src.toLowerCase().endsWith('.svg') || src.startsWith('data:image/svg');
    }

    const staticSrc = src && typeof src === 'object' && 'src' in src ? (src as {src: string}).src : undefined;
    return typeof staticSrc === 'string' && staticSrc.toLowerCase().endsWith('.svg');
};

const OptimizedImage = ({src, alt, sizes, placeholder, ...rest}: OptimizedImageProps) => {
    const isStatic = typeof src === 'object' && src !== null && 'src' in src;
    const isSvg = isSvgSrc(src);

    const explicitWidth = (rest as {width?: number}).width;
    const explicitHeight = (rest as {height?: number}).height;

    const isSmall =
        (typeof explicitWidth === 'number' && explicitWidth < 40) || (typeof explicitHeight === 'number' && explicitHeight < 40);

    if (isSvg) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {fill: fillProp, quality, blurDataURL, suppressHydrationWarning, fetchPriority, loading, priority, ...imgRest} = rest as any;

        // IMPORTANT: use the locally destructured 'fillProp', NOT (rest as any).fill
        // because 'fill' has already been removed from rest at this point.
        const isFillMode = fillProp === true;

        const svgSrc = isStatic ? (src as StaticImageData).src : (src as string);

        return (
            // SVGs skip the /_next/image optimizer anyway, so a plain <img> avoids next/image's extra wrapper and JS.
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={svgSrc}
                alt={alt}
                width={isFillMode ? undefined : explicitWidth}
                height={isFillMode ? undefined : explicitHeight}
                loading={priority ? 'eager' : loading ?? 'lazy'}
                // Lowercase on purpose: React 18.2 doesn't know the camelCase `fetchPriority` prop and
                // warns about it on a plain <img>. next/image does the same conversion internally.
                {...{fetchpriority: priority ? 'high' : fetchPriority ?? 'auto'}}
                suppressHydrationWarning={true}
                style={isFillMode ? {position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain'} : imgRest.style}
                {...(isFillMode ? {} : imgRest)}
            />
        );
    }

    // For icons < 40px, clamp sizes to the explicit pixel dimension so the browser
    // never requests a device-width-sized variant (e.g. w=640) for a 24px flag.
    const defaultSizes = sizes
        ? sizes
        : isSmall && explicitWidth
        ? `${explicitWidth}px`
        : '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';

    let computedPlaceholder = placeholder || (!isSmall && isStatic && (src as StaticImageData).blurDataURL ? 'blur' : 'empty');

    if (computedPlaceholder === 'blur' && !rest.blurDataURL && (!isStatic || !(src as StaticImageData).blurDataURL)) {
        computedPlaceholder = 'empty';
    }

    if (process.env.NODE_ENV === 'development' && !isStatic && !rest.fill && (!rest.width || !rest.height)) {
        console.warn(
            `[OptimizedImage] src="${src}" is a string URL but is missing width+height or fill. ` +
                `This causes Cumulative Layout Shift (CLS). Add width={N} height={N} or fill={true} with a positioned container.`
        );
    }

    const autoStyle = isStatic && !(rest as any).fill && !rest.width && !rest.height ? {width: 'auto', height: 'auto'} : {};

    return (
        <Image
            src={src}
            alt={alt}
            sizes={defaultSizes}
            placeholder={computedPlaceholder}
            {...rest}
            style={{...autoStyle, ...rest.style}}
            // 80 by default; a caller's `quality` (e.g. screenshots with small UI text) wins.
            quality={rest.quality ?? 80}
            suppressHydrationWarning={true}
        />
    );
};

export default memo(OptimizedImage);
